'use client';

/**
 * Sign-up page (passwordless, email + 6-digit code).
 *
 * Mirrors lexai-superadmin/src/app/login/page.tsx + /sign-in — first-time
 * emails get an account provisioned on verify; existing emails just sign in.
 */
import Image from 'next/image';
import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import { type FormEvent, useState } from 'react';

import { CountrySelect } from '@/components/auth';
import { ApiError, AuthError, RateLimitError } from '@/lib/api';
import {
  COUNTRY_OTHER,
  requestLoginCode,
  requestSignupCode,
  safeRedirectPath,
  useCountryCatalogue,
  verifyLoginCode,
} from '@/lib/auth';
import { useBillingPlans } from '@/lib/billing';

type Step = 'email' | 'code';

/** Named so the renderer can put the error on the field it belongs to. */
const COUNTRY_REQUIRED = 'Select the country your organisation is based in.';

export default function SignUpPage() {
  const router = useRouter();
  const params = useSearchParams();
  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [profession, setProfession] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [country, setCountry] = useState('');
  const [phone, setPhone] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [code, setCode] = useState('');
  // Company / job title / phone start collapsed. They are the three fields
  // nobody has to answer, and showing all seven at once was what pushed the
  // card past the viewport.
  const [showOptional, setShowOptional] = useState(false);
  // Trial length is a plan setting a super admin owns, not a number to write
  // into marketing copy. `/billing/plans` is public and edition-resolved, and
  // the signup handler takes the first active public plan's `trial_days`
  // ordered by sort_order — which is the order this endpoint returns. Read the
  // same row, and say nothing at all when it is zero.
  const { data: publicPlans } = useBillingPlans();
  const trialDays = publicPlans?.[0]?.trial_days ?? 0;
  const [busy, setBusy] = useState(false);
  const [resending, setResending] = useState(false);
  const [info, setInfo] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  // Only the countries the corpus actually holds law for — offering the
  // rest would imply local-law depth we don't have. See useCountryCatalogue.
  const { options: countries, isLoading: countriesLoading } = useCountryCatalogue();
  const selectedCountry = countries.find((c) => c.code === country);

  async function onRequestCode(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setErr(null);
    setInfo(null);

    if (!country) {
      // The picker is a <button>, so the browser's own required-field
      // validation does not reach it — say so rather than posting a signup
      // with no country and silently provisioning an unknown jurisdiction.
      setErr(COUNTRY_REQUIRED);
      setBusy(false);
      return;
    }

    const normalised = email.trim().toLowerCase();
    try {
      await requestSignupCode(normalised, {
        fullName: fullName.trim() || undefined,
        profession: profession.trim() || undefined,
        companyName: companyName.trim() || undefined,
        // "Other" is a UI sentinel, not a country: send nothing and let the
        // backend record an honest unknown jurisdiction rather than a guess.
        country: country && country !== COUNTRY_OTHER ? country : undefined,
        phone: phone.trim() || undefined,
        jobTitle: jobTitle.trim() || undefined,
      });
      setEmail(normalised);
      setStep('code');
      // The step-2 subhead already names the address and the expiry; the info
      // line under the field is reserved for resend feedback.
      setInfo(null);
    } catch (e2) {
      if (e2 instanceof RateLimitError) {
        setErr('Too many attempts. Try again in 15 minutes.');
      } else if (e2 instanceof ApiError) {
        setErr(e2.message);
      } else {
        setErr('Could not send code.');
      }
    } finally {
      setBusy(false);
    }
  }

  async function onVerifyCode(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setErr(null);

    try {
      await verifyLoginCode(email, code.trim());
      router.replace(safeRedirectPath(params.get('redirect_url')));
    } catch (e2) {
      if (e2 instanceof AuthError) {
        setErr('Invalid or expired code.');
      } else if (e2 instanceof RateLimitError) {
        setErr('Too many attempts. Request a new code.');
      } else if (e2 instanceof ApiError) {
        setErr(e2.message);
      } else {
        setErr('Could not create your workspace.');
      }
      setBusy(false);
    }
  }

  async function onResend(): Promise<void> {
    if (resending) return;
    setResending(true);
    setErr(null);
    setInfo(null);
    try {
      await requestLoginCode(email);
      setInfo('A new code is on its way.');
    } catch (e2) {
      if (e2 instanceof ApiError) setErr(e2.message);
      else setErr('Could not resend the code.');
    } finally {
      setResending(false);
    }
  }

  return (
    <div>
      <div className="eyebrow mb-2" style={{ color: 'var(--cu)' }}>
        {step === 'email' ? 'Step 1 of 2 · Create account' : 'Step 2 of 2 · Verify email'}
      </div>
      <h1 className="h-display-3 mb-1" style={{ fontSize: 30 }}>
        {step === 'email' ? (
          <>
            Set up your legal intelligence <em>workspace</em>
          </>
        ) : (
          <>
            Enter the six-digit <em>code</em>
          </>
        )}
      </h1>
      <p className="muted text-[13px] mb-6">
        {step === 'email'
          ? `No password. We email you a one-time code — verify it and your workspace is ready.${
              trialDays > 0 ? ` Includes a ${trialDays}-day free trial.` : ''
            }`
          : `We sent a code to ${email}. It expires in 10 minutes.`}
      </p>

      {step === 'email' ? (
        <form onSubmit={onRequestCode}>
          {/* Name and profession pair up: both are short, and stacking every
              field was what made the card outgrow the viewport. */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
            <div className="flex flex-col gap-1">
              <label className="eyebrow" htmlFor="signup-name">
                Full name
              </label>
              <input
                autoFocus
                required
                autoComplete="name"
                className="input"
                id="signup-name"
                name="name"
                placeholder="Alex Morgan"
                type="text"
                value={fullName}
                onChange={(ev) => setFullName(ev.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="eyebrow" htmlFor="signup-profession">
                Profession
              </label>
              <select
                required
                className="input"
                id="signup-profession"
                name="profession"
                value={profession}
                onChange={(ev) => setProfession(ev.target.value)}
              >
                <option value="">Select your profession</option>
                <option value="lawyer">Lawyer / Advocate</option>
                <option value="in_house_counsel">In-house Counsel</option>
                <option value="ca">Chartered Accountant</option>
                <option value="cs">Company Secretary</option>
                <option value="compliance">Compliance / Risk</option>
                <option value="founder">Founder / Business Owner</option>
                <option value="procurement">Procurement / Operations</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>

          <div className="flex flex-col gap-1 mb-3">
            <label className="eyebrow" htmlFor="signup-email">
              Work email
            </label>
            <input
              required
              autoComplete="email"
              className={`input${err && err !== COUNTRY_REQUIRED ? ' is-error' : ''}`}
              id="signup-email"
              name="email"
              placeholder="you@yourfirm.com"
              type="email"
              value={email}
              onChange={(ev) => setEmail(ev.target.value)}
            />
          </div>

          {/* Country is a short value — a half-width control reads as a choice,
              a full-width one reads as free text. The grid collapses on narrow
              screens, where half a card is too little. */}
          <div className="mb-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="flex flex-col gap-1">
                <span className="eyebrow" id="signup-country-label">
                  Country
                </span>
                <CountrySelect
                  id="signup-country"
                  invalid={err === COUNTRY_REQUIRED}
                  labelledBy="signup-country-label"
                  loading={countriesLoading}
                  options={countries}
                  otherValue={COUNTRY_OTHER}
                  value={country}
                  onChange={setCountry}
                />
              </div>
            </div>
            {selectedCountry && selectedCountry.covers.length > 0 ? (
              <span className="muted text-[12px] block mt-1">
                Local-law depth covers {selectedCountry.covers.join(', ')}.
              </span>
            ) : null}
            {country === COUNTRY_OTHER ? (
              <span className="muted text-[12px] block mt-1">
                We&rsquo;ll analyse your contracts against general commercial-contract
                principles until we cover your jurisdiction. Billing is in USD.
              </span>
            ) : null}
          </div>

          <div className="lex-auth-rule" />

          <button
            aria-controls="signup-optional"
            aria-expanded={showOptional}
            className="lex-auth-optional"
            type="button"
            onClick={() => setShowOptional((v) => !v)}
          >
            <span aria-hidden="true" className="lex-auth-optional__sign">
              {showOptional ? '−' : '+'}
            </span>
            {showOptional ? 'Hide optional details' : 'Add optional details'}
          </button>

          {showOptional ? (
            <div className="mt-3" id="signup-optional">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                <div className="flex flex-col gap-1">
                  <label className="eyebrow" htmlFor="signup-company">
                    Company name
                  </label>
                  <input
                    autoComplete="organization"
                    className="input"
                    id="signup-company"
                    name="company"
                    placeholder="Your company or firm"
                    type="text"
                    value={companyName}
                    onChange={(ev) => setCompanyName(ev.target.value)}
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <label className="eyebrow" htmlFor="signup-job">
                    Job title
                  </label>
                  <input
                    autoComplete="organization-title"
                    className="input"
                    id="signup-job"
                    name="job_title"
                    placeholder="e.g. General Counsel"
                    type="text"
                    value={jobTitle}
                    onChange={(ev) => setJobTitle(ev.target.value)}
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                <div className="flex flex-col gap-1">
                  <label className="eyebrow" htmlFor="signup-phone">
                    Phone number
                  </label>
                  <input
                    autoComplete="tel"
                    className="input"
                    id="signup-phone"
                    name="phone"
                    placeholder="Include country code"
                    type="tel"
                    value={phone}
                    onChange={(ev) => setPhone(ev.target.value)}
                  />
                </div>
              </div>
            </div>
          ) : null}

          {/* The error used to live inside the job-title group, which is now
              collapsed by default — it would have been invisible exactly when
              a country or email error needed reading. */}
          {err ? (
            <p className="text-[12px] mt-3 mb-1" role="alert" style={{ color: 'var(--red)' }}>
              {err}
            </p>
          ) : null}

          <div className="mt-4">
            <button
              className="btn btn-p btn-lg"
              disabled={busy}
              style={{ width: '100%' }}
              type="submit"
            >
              {busy ? 'Sending…' : 'Send code'}
            </button>
          </div>
        </form>
      ) : (
        <form onSubmit={onVerifyCode}>
          <div className="flex flex-col gap-1 mb-4">
            <label className="eyebrow" htmlFor="signup-code">
              6-digit code
            </label>
            <input
              autoFocus
              required
              autoComplete="one-time-code"
              className={`input${err ? ' is-error' : ''}`}
              id="signup-code"
              inputMode="numeric"
              maxLength={6}
              minLength={6}
              name="code"
              pattern="\d{6}"
              style={{ textAlign: 'center', letterSpacing: '0.5em', fontSize: 18 }}
              type="text"
              value={code}
              onChange={(ev) => setCode(ev.target.value.replace(/\D/g, '').slice(0, 6))}
            />
            {info ? (
              <span className="muted text-[12px]" style={{ marginTop: 4 }}>
                {info}
              </span>
            ) : null}
            {err ? (
              <span className="text-[12px]" style={{ color: 'var(--red)' }}>
                {err}
              </span>
            ) : null}
          </div>
          <button
            className="btn btn-p btn-lg"
            disabled={busy || code.length !== 6}
            style={{ width: '100%' }}
            type="submit"
          >
            {busy ? 'Creating…' : 'Create account'}
          </button>
          <div
            className="text-[12px]"
            style={{ display: 'flex', justifyContent: 'space-between', marginTop: 12 }}
          >
            <button
              className="lex-auth-link"
              disabled={resending}
              type="button"
              onClick={onResend}
            >
              {resending ? 'Sending…' : 'Resend code'}
            </button>
            <button
              className="lex-auth-link is-muted"
              type="button"
              onClick={() => {
                setStep('email');
                setCode('');
                setErr(null);
                setInfo(null);
              }}
            >
              Use a different email
            </button>
          </div>
        </form>
      )}

      {/* Mirrors /sign-in: a labelled rule carrying the wordmark, then the
          counterpart action as an outline button. Hidden once the code has
          been sent — at that point the account is already being created. */}
      {step === 'email' ? (
        <>
          <div className="lex-auth-alt">
            <span>
              Already using{' '}
              <Image
                alt="LexVio.ai"
                className="lex-auth-alt__logo"
                height={16}
                src="/logo.png"
                width={43}
              />
              ?
            </span>
          </div>
          <Link className="btn btn-s btn-lg" href="/sign-in" style={{ width: '100%' }}>
            Sign in
          </Link>
        </>
      ) : null}
      <p className="muted text-[11px] mt-4">
        By creating an account you agree to our{' '}
        <Link className="lex-auth-link" href="/legal/terms" style={{ color: 'var(--cu)' }}>
          Terms
        </Link>{' '}
        and{' '}
        <Link className="lex-auth-link" href="/legal/privacy" style={{ color: 'var(--cu)' }}>
          Privacy Policy
        </Link>
        .
      </p>
    </div>
  );
}
